// The sequence as one dated timeline (Phase 5.2): Day 1 Email 1, Day 2 LinkedIn connect… One step open at a time,
// edit inline, warnings next to the line they refer to, the signature shown once at the end.
import { useRef, useState } from "react";
import { Mail, MessageSquare, OctagonX, Phone, TriangleAlert, UserPlus } from "lucide-react";
import { Badge, Button, cx, ErrorNote, inputCls, useAction } from "./ui";

export interface Step {
  key: string; // email:0 | call:1 | linkedin_note | linkedin_message
  label: string;
  day: number; // business day (1-based: Email 1 = Day 1)
  type: "email" | "call" | "linkedin_connect" | "linkedin_message";
  subject?: string;
  subjectLocked?: boolean;
  body: string;
  findings: { rule: string; match?: string; severity: "block" | "warn"; line?: number; where?: string }[];
  editable: boolean;
  badge?: string;
  sent?: boolean;
}

const ICON = { email: Mail, call: Phone, linkedin_connect: UserPlus, linkedin_message: MessageSquare };

/** Template steps → timeline steps for a master (or a rendered sequence). */
export function buildSteps(
  template: { type: string; label: string; dayOffset: number }[],
  pieces: { emails: { subject: string; body: string }[]; callScripts: string[]; linkedinNote: string; linkedinMessage: string },
  findings: Step["findings"],
  opts: { editable: boolean | ((key: string) => boolean); badge?: (key: string) => string | undefined; sent?: (label: string) => boolean } = { editable: false },
): Step[] {
  return template.map((t) => {
    const n = Number(t.label.replace(/\D/g, "")) - 1;
    const key = t.type === "email" ? `email:${n}` : t.type === "call" ? `call:${n}` : t.type === "linkedin_connect" ? "linkedin_note" : "linkedin_message";
    const where = t.type === "email" ? `Email ${n + 1}` : t.type === "call" ? `Call ${n + 1}` : t.type === "linkedin_connect" ? "LinkedIn note" : "LinkedIn message";
    const body = t.type === "email" ? pieces.emails[n]?.body ?? "" : t.type === "call" ? pieces.callScripts[n] ?? "" : t.type === "linkedin_connect" ? pieces.linkedinNote : pieces.linkedinMessage;
    return {
      key,
      label: t.label,
      day: t.dayOffset + 1,
      type: t.type as Step["type"],
      subject: t.type === "email" ? pieces.emails[n]?.subject : undefined,
      subjectLocked: t.type === "email" && n > 0,
      body,
      findings: findings.filter((f) => (f.where ?? "").startsWith(where)),
      editable: typeof opts.editable === "function" ? opts.editable(key) : opts.editable,
      badge: opts.badge?.(key),
      sent: opts.sent?.(t.label),
    };
  });
}

export function SequenceTimeline({ steps, onSave, footer, threadSubject }: { steps: Step[]; onSave?: (step: Step, value: { subject?: string; body: string }, editSeconds: number) => Promise<void>; footer?: React.ReactNode; threadSubject?: string }) {
  const [open, setOpen] = useState<string>(steps[0]?.key ?? "");
  return (
    <ol className="relative space-y-0">
      {steps.map((s, i) => (
        <li key={s.key} className="relative pl-8">
          <span className={cx("absolute left-[11px] top-0 w-px bg-line", i === steps.length - 1 ? "h-5" : "h-full")} aria-hidden />
          <StepRow step={s} open={open === s.key} onToggle={() => setOpen(open === s.key ? "" : s.key)} onSave={onSave} threadSubject={threadSubject} />
        </li>
      ))}
      {footer && <li className="pl-8 pt-2">{footer}</li>}
    </ol>
  );
}

function StepRow({ step, open, onToggle, onSave, threadSubject }: { step: Step; open: boolean; onToggle: () => void; onSave?: (step: Step, value: { subject?: string; body: string }, editSeconds: number) => Promise<void>; threadSubject?: string }) {
  const Icon = ICON[step.type];
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(step.subject ?? "");
  const [body, setBody] = useState(step.body);
  const started = useRef(0);
  const save = useAction();
  const blocks = step.findings.filter((f) => f.severity === "block");
  const warns = step.findings.filter((f) => f.severity === "warn");
  const lines = step.body.split("\n");
  const byLine = new Map<number, Step["findings"]>();
  const general: Step["findings"] = [];
  for (const f of step.findings) if (f.line && f.line <= lines.length && !/subject/.test(f.where ?? "")) byLine.set(f.line, [...(byLine.get(f.line) ?? []), f]);
  else general.push(f);
  return (
    <div className="pb-3">
      <button onClick={onToggle} className="flex w-full items-center gap-3 rounded-lg py-2 text-left hover:bg-surface-2" aria-expanded={open}>
        <span className={cx("absolute left-0 flex h-6 w-6 items-center justify-center rounded-full border bg-surface", blocks.length ? "border-bad text-bad" : "border-line text-ink-2")}>
          <Icon size={13} />
        </span>
        <span className="w-14 shrink-0 text-xs font-medium text-muted">Day {step.day}</span>
        <span className="text-sm font-medium text-ink">{step.label}</span>
        {step.subject && step.type === "email" && <span className="min-w-0 truncate text-sm text-muted">{step.subjectLocked ? `Re: ${threadSubject ?? ""}` : step.subject}</span>}
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {step.badge && <Badge tone="customized">{step.badge}</Badge>}
          {step.sent && <Badge tone="sent">sent</Badge>}
          {blocks.length > 0 && <span className="flex items-center gap-0.5 text-xs text-bad"><OctagonX size={13} /> {blocks.length}</span>}
          {warns.length > 0 && <span className="flex items-center gap-0.5 text-xs text-warn"><TriangleAlert size={13} /> {warns.length}</span>}
        </span>
      </button>
      {open && (
        <div className="ml-14 mt-1 rounded-lg border border-line bg-surface p-4">
          {editing ? (
            <div className="space-y-2">
              {step.type === "email" && <input className={inputCls} value={step.subjectLocked ? `Re: ${threadSubject ?? ""}` : subject} disabled={step.subjectLocked} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" />}
              <textarea className={cx(inputCls, "mail min-h-56 max-w-none")} value={body} onChange={(e) => setBody(e.target.value)} aria-label={`${step.label} text`} />
              <div className="text-xs text-muted">Merge fields: {"{first_name}"} {"{company}"} {"{title}"} {"{signal_line}"}. Greeting, signature and footer are added automatically. The checks run again when you save.</div>
              <ErrorNote error={save.error} />
              <div className="flex gap-2">
                <Button tone="primary" disabled={save.busy} onClick={() => save.run(async () => { await onSave!(step, { subject: step.type === "email" && !step.subjectLocked ? subject : undefined, body }, Math.round((Date.now() - started.current) / 1000)); setEditing(false); })}>
                  Save
                </Button>
                <Button onClick={() => { setEditing(false); setBody(step.body); setSubject(step.subject ?? ""); }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              {step.type === "email" && !step.subjectLocked && <div className="mb-2 text-sm"><span className="text-muted">Subject </span><span className="font-medium text-ink">{step.subject}</span></div>}
              <div className="space-y-0.5">
                {lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
                    <div className="mail min-h-[1.2em]">{l || " "}</div>
                    <div className="space-y-0.5">
                      {(byLine.get(i + 1) ?? []).map((f, j) => (
                        <div key={j} className={cx("max-w-60 text-xs", f.severity === "block" ? "text-bad" : "text-warn")}>{f.rule}{f.match ? `: “${f.match}”` : ""}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {general.length > 0 && (
                <ul className="mt-3 space-y-0.5 border-t border-line pt-2 text-xs">
                  {general.map((f, i) => <li key={i} className={f.severity === "block" ? "text-bad" : "text-warn"}>{f.rule}{f.match ? `: “${f.match}”` : ""}</li>)}
                </ul>
              )}
              {step.editable && onSave && (
                <div className="mt-3">
                  <Button onClick={() => { started.current = Date.now(); setBody(step.body); setSubject(step.subject ?? ""); setEditing(true); }}>Edit</Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Greeting / signature / footer, shown once under the timeline. */
export function SignatureNote({ signer, postal }: { signer: { name: string; title?: string; phone?: string }; postal: string }) {
  return (
    <div className="ml-14 rounded-lg border border-dashed border-line p-4 text-sm">
      <div className="mb-1 text-xs font-medium text-muted">Every email starts with “{"{first_name}"},” and ends with</div>
      <div className="mail text-ink-2">
        {signer.name || <span className="text-bad">rep name missing</span>}
        {"\n"}
        {signer.title ? `${signer.title}, Intellias` : <span className="text-bad">title missing</span>}
        {"\n"}
        {signer.phone || <span className="text-bad">phone missing</span>}
        {"\n\n"}Not the right fit? Just reply and I won't follow up. Intellias, {postal}
      </div>
    </div>
  );
}
